import { VentasResumenProductoRow } from '../services/ventasInformesService';

export const SIN_CLASIFICAR = '(Sin clasificar)';

export type TotalesAgregados = {
  cantidad: number;
  montoTotal: number;
  costoTotal: number;
  pesoTotal: number;
  volumenTotal: number;
};

export type FilaAgregada = TotalesAgregados & {
  nombre: string;
  pctMonto: number;
};

export type NivelClasificacion = 'ramo' | 'linea' | 'clase';

export type CampoClasificacion = 'nombreRamo' | 'nombreLinea' | 'nombreClase';

export function normalizarClasificacion(val?: string | null): string {
  const t = (val ?? '').trim();
  return t || SIN_CLASIFICAR;
}

export function sumRows(items: VentasResumenProductoRow[]): TotalesAgregados {
  return items.reduce(
    (acc, r) => ({
      cantidad: acc.cantidad + r.cantidad,
      montoTotal: acc.montoTotal + r.montoTotal,
      costoTotal: acc.costoTotal + (r.costoTotal ?? 0),
      pesoTotal: acc.pesoTotal + r.pesoTotal,
      volumenTotal: acc.volumenTotal + r.volumenTotal,
    }),
    { cantidad: 0, montoTotal: 0, costoTotal: 0, pesoTotal: 0, volumenTotal: 0 }
  );
}

export function pctRentabilidad(monto: number, costo: number): number | null {
  if (Math.abs(monto) < 1e-9) return null;
  return ((monto - costo) / monto) * 100;
}

export function pctDelMonto(monto: number, totalMonto: number): number {
  if (Math.abs(totalMonto) < 1e-9) return 0;
  return (monto / totalMonto) * 100;
}

export function aggregateByField(
  rows: VentasResumenProductoRow[],
  field: CampoClasificacion,
  totalMonto: number
): FilaAgregada[] {
  const map = new Map<string, VentasResumenProductoRow[]>();
  for (const r of rows) {
    const key = normalizarClasificacion(r[field]);
    const list = map.get(key) ?? [];
    list.push(r);
    map.set(key, list);
  }
  const result: FilaAgregada[] = [];
  for (const [nombre, items] of Array.from(map.entries())) {
    const t = sumRows(items);
    result.push({
      nombre,
      ...t,
      pctMonto: pctDelMonto(t.montoTotal, totalMonto),
    });
  }
  return result.sort((a, b) => b.montoTotal - a.montoTotal);
}

export function conteosClasificacionUnicos(rows: VentasResumenProductoRow[]) {
  const ramos = new Set<string>();
  const lineas = new Set<string>();
  const clases = new Set<string>();
  for (const r of rows) {
    ramos.add(normalizarClasificacion(r.nombreRamo));
    lineas.add(normalizarClasificacion(r.nombreLinea));
    clases.add(normalizarClasificacion(r.nombreClase));
  }
  return { ramos: ramos.size, lineas: lineas.size, clases: clases.size };
}

export function filasAgregadasToExport(
  filas: FilaAgregada[],
  nivel: NivelClasificacion
): Record<string, string | number>[] {
  const pref = nivel === 'ramo' ? 'Ramo' : nivel === 'linea' ? 'Línea' : 'Clase';
  return filas.map((f) => ({
    [pref]: f.nombre,
    Cantidad: f.cantidad,
    'Monto Total': f.montoTotal,
    '% Monto': Math.round(f.pctMonto * 100) / 100,
    'Costo Total': f.costoTotal,
    '% Rentabilidad': (() => {
      const pct = pctRentabilidad(f.montoTotal, f.costoTotal);
      return pct != null ? Math.round(pct * 10) / 10 : '';
    })(),
    'Peso Total (kg)': f.pesoTotal,
    'Volumen Total (m³)': f.volumenTotal,
  }));
}
